import { useCallback, useContext, useMemo } from 'react'

import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import PropertyRateOption from './PropertyRateOption'
import PropertyRefundabilitySelector from './PropertyRefundabilitySelector'

interface Props {
  lpcOffer?: App.HotelOffer
  rates: Array<App.EnrichedPropertyRate>
  defaultRateId: string
  /** The property room's real id, distinct from `roomListId` (an analytics-only grouping id). */
  roomId: string
  roomListId: string
  roomPosition: number
  rateListId: string
  propertyTimezone: string
}

/**
 * Merged variant that combines property rates with radio button selection.
 *
 * New UI structure: Radio selector for rates (by rate ID) injected into PropertyRateOption where the cancellation policy would normally go
 */
function PropertyRateOptionMerged(props: Props) {
  const {
    lpcOffer,
    rates,
    defaultRateId,
    roomId,
    roomListId,
    roomPosition,
    rateListId,
    propertyTimezone,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  // Lives in `OfferPageState` (rather than local state) so the assistant's page-snapshot builder
  // can read which rate the customer currently has selected -- see `viewedRateIdByRoomId`'s doc
  // comment in `offerPageStateReducer.tsx`. Single source of truth: this room's own radio group is
  // the only writer, and reads its own value back rather than keeping a separate local copy.
  // Keyed by `roomId` (the real property room id), not `roomListId` (an analytics-only id shared
  // across every room in the list).
  const selectedRateId = useContext(OfferPageStateContext).viewedRateIdByRoomId?.[roomId] ?? defaultRateId
  const setSelectedRateId = useCallback((rateId: string) => {
    offerPageDispatch({ type: OfferPageActions.setViewedRoomRateId, roomId, rateId })
  }, [offerPageDispatch, roomId])

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const hasChoice = rates.length > 1

  if (!activeRate) {
    return null
  }

  const cancellationPolicyContentOverride =
    hasChoice ?
        <PropertyRefundabilitySelector
          rates={rates}
          selectedRateId={selectedRateId}
          onChange={setSelectedRateId}
          propertyTimezone={propertyTimezone}
        /> :
      undefined

  return <PropertyRateOption
    lpcOffer={lpcOffer}
    rate={activeRate}
    roomListId={roomListId}
    roomPosition={roomPosition}
    rateListId={rateListId}
    ratePosition={activeRate.defaultOrder + 1}
    cancellationPolicyContentOverride={cancellationPolicyContentOverride}
  />
}

export default PropertyRateOptionMerged
